import {
  CircularProgress,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useRef, useState } from 'react';
import matchingWeightsAPI, {
  CriteriaName,
  TenantCriteriaWeight,
  TenantCriteriaWeightPayload,
} from '../../api/matching-weights';
import { BeforeUnload, SnackMessage } from '../../components/common';
import Button from '../../components/common/button';
import PageLoader from '../../components/common/page-loader';
import { COLORS } from '../../theme/variables';

interface CriteriaConfig {
  name: CriteriaName;
  label: string;
  defaultWeight: number;
  purpose: string;
}

const MATCHING_CRITERIA: CriteriaConfig[] = [
  {
    name: 'SpecializationMatch',
    label: 'Specialization Match',
    defaultWeight: 10,
    purpose: 'Rewards overlapping specializations between mentor and venture',
  },
  {
    name: 'MentorVotes',
    label: 'Mentor Votes',
    defaultWeight: 10,
    purpose: 'Rewards mentors who voted for a venture',
  },
  {
    name: 'GeographicalProximity',
    label: 'Geographical Proximity',
    defaultWeight: 10,
    purpose: 'Rewards same city (10) or same state (5) proximity',
  },
  {
    name: 'NumberOfMentorAssignments',
    label: 'Number of Mentor Assignments',
    defaultWeight: 10,
    purpose: 'Rewards mentors with fewer than 3 existing assignments',
  },
  {
    name: 'EmbeddingMatch',
    label: 'Embedding Match',
    defaultWeight: 10,
    purpose: 'AI embedding similarity (top 3: 100, top 5: 50, top 10: 10)',
  },
  {
    name: 'PreviousMatchOffer',
    label: 'Previous Match Offer',
    defaultWeight: 1,
    purpose: 'Slight penalty for repeated match offers (-10 per prior offer)',
  },
  {
    name: 'AssignmentHistory',
    label: 'Assignment History',
    defaultWeight: -10,
    purpose: 'Penalizes mentors previously assigned to the venture',
  },
  {
    name: 'ExistingAssignment',
    label: 'Existing Assignment',
    defaultWeight: -1000,
    purpose: 'Heavy penalty if mentor is currently assigned to the venture',
  },
  {
    name: 'MentorManagementRequests',
    label: 'Mentor Management Requests',
    defaultWeight: -1000,
    purpose: 'Heavy penalty if a removal/change request exists',
  },
];

function buildDefaultWeights(): Record<CriteriaName, number> {
  const result = {} as Record<CriteriaName, number>;
  for (const c of MATCHING_CRITERIA) {
    result[c.name] = c.defaultWeight;
  }
  return result;
}

function weightsFromServer(
  data: TenantCriteriaWeight[],
): Record<CriteriaName, number> {
  const defaults = buildDefaultWeights();
  for (const w of data) {
    if (w.criteriaName in defaults) {
      defaults[w.criteriaName] = w.weight;
    }
  }
  return defaults;
}

const INPUT_PROPS = { min: -10000, max: 10000, step: 1 };

const useStyles = makeStyles({
  container: {
    maxWidth: 670,
  },
  title: {
    margin: '0 0 20px',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
  },
  row: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 16,

    '@media (max-width: 600px)': {
      flexDirection: 'column',
    },
  },
  labelContainer: {
    flex: 1,
    minWidth: 0,
  },
  label: {
    color: COLORS.COLOR_TEXT_BASE,
    fontWeight: 500,
  },
  purpose: {
    color: COLORS.COLOR_TEXT_LIGHTENED_20,
    fontSize: 13,
    marginTop: 4,
  },
  input: {
    width: 120,
    flexShrink: 0,
  },
  actions: {
    marginTop: 32,
  },
});

function TenantMatchingWeightsPage() {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [weights, setWeights] =
    useState<Record<CriteriaName, number>>(buildDefaultWeights);
  const [initialWeights, setInitialWeights] =
    useState<Record<CriteriaName, number>>(buildDefaultWeights);
  const serverWeightsRef = useRef<TenantCriteriaWeight[]>([]);

  const dirty = JSON.stringify(weights) !== JSON.stringify(initialWeights);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const data = await matchingWeightsAPI.getWeights();
        if (cancelled) return;
        serverWeightsRef.current = data;
        const parsed = weightsFromServer(data);
        setWeights(parsed);
        setInitialWeights(parsed);
      } catch (_e) {
        if (cancelled) return;
        enqueueSnackbar('Failed to load matching weights. Please try again.', {
          variant: 'error',
        });
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [enqueueSnackbar]);

  const handleWeightChange = useCallback(
    (name: CriteriaName, value: string) => {
      const num = value === '' ? 0 : Math.round(Number(value));
      const clamped = Math.min(10000, Math.max(-10000, num));
      setWeights((prev) => ({
        ...prev,
        [name]: clamped,
      }));
    },
    [],
  );

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const payload: TenantCriteriaWeightPayload[] = MATCHING_CRITERIA.map(
        (criteria) => {
          const existing = serverWeightsRef.current.find(
            (w) => w.criteriaName === criteria.name,
          );
          if (existing) {
            return { ...existing, weight: weights[criteria.name] };
          }
          return {
            criteriaName: criteria.name,
            weight: weights[criteria.name],
          };
        },
      );

      const updated = await matchingWeightsAPI.updateWeights(payload);
      serverWeightsRef.current = updated;
      const parsed = weightsFromServer(updated);
      setWeights(parsed);
      setInitialWeights(parsed);
      enqueueSnackbar('Matching weights saved successfully', {
        variant: 'success',
      });
    } catch (e: unknown) {
      const messageError = (e as AxiosError<{ message?: string }>).response
        ?.data?.message;
      enqueueSnackbar(
        'An error occurred while saving matching weights. Please try again.',
        {
          content: (key, message) =>
            SnackMessage({
              key,
              message,
              variant: 'error',
              additionalMessage: messageError,
            }),
          variant: 'error',
        },
      );
    } finally {
      setSaving(false);
    }
  }, [enqueueSnackbar, weights]);

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div className={classes.container}>
      <BeforeUnload
        when={dirty && !saving}
        title='Leave the page'
        body='You are about to leave the page, all unsaved changes will be lost. Do you want to continue?'
      />
      <Typography className={classes.title} variant='h4'>
        Advanced Matching Weights
      </Typography>
      <div className={classes.list}>
        {MATCHING_CRITERIA.map((criteria) => (
          <div key={criteria.name} className={classes.row}>
            <div className={classes.labelContainer}>
              <Typography className={classes.label}>
                {criteria.label}
              </Typography>
              <Typography className={classes.purpose}>
                {criteria.purpose}
              </Typography>
            </div>
            <TextField
              className={classes.input}
              type='number'
              variant='outlined'
              size='small'
              value={weights[criteria.name]}
              onChange={(e) =>
                handleWeightChange(criteria.name, e.target.value)
              }
              inputProps={{
                ...INPUT_PROPS,
                'aria-label': `Weight for ${criteria.label}`,
              }}
              data-testid={`weight-${criteria.name}`}
            />
          </div>
        ))}
      </div>
      <div className={classes.actions}>
        <Button
          onClick={handleSave}
          disabled={!dirty || saving}
          data-testid='save-matching-weights'
        >
          {saving ? <CircularProgress size={24} color='inherit' /> : 'Save'}
        </Button>
      </div>
    </div>
  );
}

export default TenantMatchingWeightsPage;
